import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { createHash } from 'crypto';
import fs from 'fs';
import path from 'path';

/**
 * Register security-focused MCP tools.
 * Tools for validating specs against approved-specs and managing the allowlist.
 */
export function registerSecurityTools(server: McpServer): void {
  // Tool: Validate a spec against approved-specs
  server.tool(
    'stackwright_pro_validate_spec',
    'Validate an OpenAPI spec against the enterprise approved-specs configuration. ' +
      'Checks if the spec URL is on the allowlist and verifies SHA-256 hash integrity. ' +
      'Use this in enterprise environments where only pre-approved API specs are allowed. ' +
      'Fails build if spec is not approved or has been modified.',
    {
      specPath: z.string().describe('URL or file path to the OpenAPI spec to validate'),
      configPath: z
        .string()
        .optional()
        .describe('Path to stackwright.yml with prebuild.security config'),
    },
    async ({ specPath, configPath }) => {
      // Try to load config from stackwright.yml
      let securityEnabled = false;
      const allowlist: Array<{ name: string; url: string; sha256: string }> = [];

      const configFile = configPath || path.join(process.cwd(), 'stackwright.yml');

      if (fs.existsSync(configFile)) {
        try {
          const content = fs.readFileSync(configFile, 'utf8');

          // Simple regex extraction for security config
          const securityMatch = content.match(
            /prebuild:\s*\n\s*security:\s*\n\s*enabled:\s*(true|false)/
          );
          if (securityMatch && securityMatch[1] === 'true') {
            securityEnabled = true;
            // Extract allowlist entries
            const specMatches = content.matchAll(
              /- name:\s*(.+?)\n\s+url:\s*(.+?)\n\s+sha256:\s*(.+?)(?:\n|$)/g
            );
            for (const match of specMatches) {
              allowlist.push({
                name: match[1]!.trim(),
                url: match[2]!.trim(),
                sha256: match[3]!.trim(),
              });
            }
          }
        } catch {
          // Config exists but couldn't parse - continue without it
        }
      }

      // If no security config, just return success (security not enabled)
      if (!securityEnabled) {
        return {
          content: [
            {
              type: 'text' as const,
              text: `✅ Spec validation skipped (security not enabled)\n\nTo enable approved-specs validation, add to stackwright.yml:\n\nprebuild:\n  security:\n    enabled: true\n    allowlist:\n      - name: My API\n        url: ${specPath}\n        sha256: <sha256-of-spec>`,
            },
          ],
        };
      }

      // Check if spec is in allowlist
      const matchingSpec = allowlist.find((s) => s.url === specPath);

      if (!matchingSpec) {
        return {
          content: [
            {
              type: 'text' as const,
              text:
                `❌ Spec rejected!\n\nError Code: SPEC_NOT_IN_ALLOWLIST\n\nURL: ${specPath}\n\nThis spec is not on the approved-specs allowlist. ` +
                `Add it using stackwright_pro_add_approved_spec before proceeding.`,
            },
          ],
          isError: true,
        };
      }

      // For file-based specs, compute hash and validate
      if (!specPath.startsWith('http://') && !specPath.startsWith('https://')) {
        if (fs.existsSync(specPath)) {
          const specContent = fs.readFileSync(specPath, 'utf8');
          const sha256 = createHash('sha256').update(specContent).digest('hex');

          if (sha256 !== matchingSpec.sha256) {
            return {
              content: [
                {
                  type: 'text' as const,
                  text: `❌ Spec hash mismatch!\n\nError Code: SPEC_HASH_MISMATCH\n\nExpected: ${matchingSpec.sha256.substring(0, 16)}...\nActual:   ${sha256.substring(0, 16)}...\n\nThe spec has been modified since it was approved. Re-approve the spec to continue.`,
                },
              ],
              isError: true,
            };
          }
        }
      }

      return {
        content: [
          {
            type: 'text' as const,
            text: `✅ Spec approved!\n\nName: ${matchingSpec.name}\nURL: ${specPath}\nStatus: Valid (${allowlist.length} specs on allowlist)`,
          },
        ],
      };
    }
  );

  // Tool: Add approved spec to allowlist
  server.tool(
    'stackwright_pro_add_approved_spec',
    'Add an OpenAPI spec to the approved-specs allowlist in stackwright.yml. ' +
      'Computes the SHA-256 hash of the spec and adds it to the security configuration. ' +
      'Use this when onboarding a new API in enterprise environments.',
    {
      name: z.string().describe('Human-readable name for the spec'),
      url: z.string().describe('URL or file path to the OpenAPI spec'),
      configPath: z.string().optional().describe('Path to stackwright.yml'),
    },
    async ({ name, url, configPath }) => {
      const configFile = configPath || path.join(process.cwd(), 'stackwright.yml');

      // Compute SHA-256 of spec
      let sha256 = '<computed-at-build>';

      try {
        if (url.startsWith('http://') || url.startsWith('https://')) {
          // URL spec - hash computed at build time; initial value already set
        } else if (fs.existsSync(url)) {
          const specContent = fs.readFileSync(url, 'utf8');
          sha256 = createHash('sha256').update(specContent).digest('hex');
        } else {
          return {
            content: [
              {
                type: 'text' as const,
                text: `❌ Spec file not found: ${url}`,
              },
            ],
            isError: true,
          };
        }
      } catch (e) {
        return {
          content: [
            {
              type: 'text' as const,
              text: `❌ Failed to read spec: ${e}`,
            },
          ],
          isError: true,
        };
      }

      const yamlSnippet = `  - name: ${name}
    url: ${url}
    sha256: ${sha256}`;

      return {
        content: [
          {
            type: 'text' as const,
            text: `📝 Add this to stackwright.yml under prebuild.security.allowlist:\n\n${yamlSnippet}\n\nSHA-256 computed: ${sha256}`,
          },
        ],
      };
    }
  );

  // Tool: List approved specs
  server.tool(
    'stackwright_pro_list_approved_specs',
    'List all specs currently on the approved-specs allowlist. ' +
      'Shows spec names, URLs, and hash prefixes. ' +
      'Use this to audit what APIs are approved in the project.',
    {
      configPath: z.string().optional().describe('Path to stackwright.yml'),
    },
    async ({ configPath }) => {
      const configFile = configPath || path.join(process.cwd(), 'stackwright.yml');

      if (!fs.existsSync(configFile)) {
        return {
          content: [
            {
              type: 'text' as const,
              text: '❌ stackwright.yml not found in project root.',
            },
          ],
          isError: true,
        };
      }

      const content = fs.readFileSync(configFile, 'utf8');

      // Check if security is enabled
      const securityEnabled =
        content.includes('prebuild:') &&
        content.includes('security:') &&
        content.includes('enabled: true');

      if (!securityEnabled) {
        return {
          content: [
            {
              type: 'text' as const,
              text: 'ℹ️ Approved-specs validation is not enabled.\n\nAdd to stackwright.yml to enable:\n\nprebuild:\n  security:\n    enabled: true\n    allowlist: []',
            },
          ],
        };
      }

      // Extract specs
      const specs: Array<{ name: string; url: string; sha256: string }> = [];
      const specMatches = content.matchAll(
        /- name:\s*(.+?)\n\s+url:\s*(.+?)\n\s+sha256:\s*(.+?)(?:\n|$)/g
      );

      for (const match of specMatches) {
        specs.push({
          name: match[1]!.trim(),
          url: match[2]!.trim(),
          sha256: match[3]!.trim().substring(0, 16) + '...',
        });
      }

      if (specs.length === 0) {
        return {
          content: [
            {
              type: 'text' as const,
              text: 'ℹ️ Security enabled but no specs on allowlist.\n\nAdd specs using stackwright_pro_add_approved_spec.',
            },
          ],
        };
      }

      const lines = [`🔐 Approved Specs (${specs.length}):\n`];
      for (const spec of specs) {
        lines.push(`📦 ${spec.name}`);
        lines.push(`   URL: ${spec.url}`);
        lines.push(`   Hash: ${spec.sha256}`);
        lines.push('');
      }

      return {
        content: [
          {
            type: 'text' as const,
            text: lines.join('\n'),
          },
        ],
      };
    }
  );
}
